import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import bs58 from 'bs58';
import { debug } from "@/lib/debug";

export interface CreatorProfile {
  id: string;
  wallet_address: string;
  username: string | null;
  username_lower: string | null;
  username_updated_at: string | null;
  bio: string | null;
  avatar_url: string | null;
  avatar_path: string | null;
  avatar_version: number;
  avatar_updated_at: string | null;
  website_url: string | null;
  twitter_handle: string | null;
  telegram_handle: string | null;
  discord_handle: string | null;
  default_slippage: number | null;
  priority_fee: string | null;
  currency_display: string | null;
  number_format: string | null;
  notifications_enabled: boolean | null;
  sound_enabled: boolean | null;
  // X/Twitter verification fields
  x_verified: boolean;
  x_twitter_id: string | null;
  x_twitter_username: string | null;
  x_verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatorProfileUpdate {
  username?: string | null;
  username_lower?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  website_url?: string | null;
  twitter_handle?: string | null;
  telegram_handle?: string | null;
  discord_handle?: string | null;
}

export function useCreatorProfile(walletAddress: string | null) {
  const [profile, setProfile] = useState<CreatorProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { signMessage, publicKey } = useTurnkeyWallet();

  const fetchProfile = useCallback(async () => {
    if (!walletAddress) {
      setProfile(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('creator_profiles')
        .select('*')
        .eq('wallet_address', walletAddress)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      setProfile(data);
    } catch (err) {
      console.error('[useCreatorProfile] Error fetching profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
    } finally {
      setIsLoading(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const upsertProfile = useCallback(async (updates: CreatorProfileUpdate): Promise<{ success: boolean; error?: string }> => {
    if (!walletAddress) {
      return { success: false, error: 'No wallet address provided' };
    }

    if (!signMessage || !publicKey) {
      return { success: false, error: 'Wallet does not support message signing' };
    }

    if (publicKey.toBase58() !== walletAddress) {
      return { success: false, error: 'Connected wallet does not match profile wallet' };
    }

    try {
      // Step 1: Request challenge from backend
      debug.trace('[useCreatorProfile] Requesting challenge...');
      const { data: challengeData, error: challengeError } = await supabase.functions.invoke('creator-profile-challenge', {
        body: { wallet_address: walletAddress },
      });

      if (challengeError) {
        console.error('[useCreatorProfile] Challenge error:', challengeError);
        // Check for rate limit
        if (challengeError.message?.includes('429') || challengeError.message?.includes('Too many')) {
          return { success: false, error: 'Too many requests. Please wait a moment and try again.' };
        }
        return { success: false, error: challengeError.message || 'Failed to get challenge' };
      }

      if (challengeData?.error) {
        console.error('[useCreatorProfile] Challenge returned error:', challengeData.error);
        if (challengeData.retry_after) {
          return { success: false, error: `Too many requests. Please wait ${challengeData.retry_after} seconds.` };
        }
        return { success: false, error: challengeData.error };
      }

      const { message, nonce, expires } = challengeData;
      if (!message || !nonce || !expires) {
        return { success: false, error: 'Invalid challenge response from server' };
      }

      debug.trace('[useCreatorProfile] Challenge received, requesting signature...');

      // Step 2: Request wallet signature
      let signature: string;
      try {
        const messageBytes = new TextEncoder().encode(message);
        const signatureBytes = await signMessage(messageBytes);
        signature = bs58.encode(signatureBytes);
      } catch (signError) {
        console.error('[useCreatorProfile] User rejected signature:', signError);
        return { success: false, error: 'Signature rejected by user' };
      }

      // Clean up twitter handle
      const cleanedUpdates = {
        ...updates,
        twitter_handle: updates.twitter_handle?.replace(/^@/, '') || null,
      };

      debug.trace('[useCreatorProfile] Signature obtained, submitting profile update...');

      // Step 3: Call upsert edge function with signature
      const { data, error: invokeError } = await supabase.functions.invoke('creator-profile-upsert', {
        body: {
          wallet_address: walletAddress,
          signature,
          message,
          updates: cleanedUpdates,
        },
      });

      if (invokeError) {
        console.error('[useCreatorProfile] Edge function error:', invokeError);
        return { success: false, error: invokeError.message || 'Failed to save profile' };
      }

      if (data?.error) {
        console.error('[useCreatorProfile] Server returned error:', data.error);
        // Provide user-friendly error messages
        if (data.error.includes('expired')) {
          return { success: false, error: 'Challenge expired. Please try again.' };
        }
        if (data.error.includes('already used')) {
          return { success: false, error: 'Challenge already used. Please try again.' };
        }
        return { success: false, error: data.error };
      }

      debug.trace('[useCreatorProfile] Profile updated successfully');

      // Refetch to get updated data
      await fetchProfile();
      return { success: true };
    } catch (err) {
      console.error('[useCreatorProfile] Error upserting profile:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save profile';
      return { success: false, error: errorMessage };
    }
  }, [walletAddress, signMessage, publicKey, fetchProfile]);

  return {
    profile,
    isLoading,
    error,
    refetch: fetchProfile,
    upsertProfile,
  };
}
